// src/components/MassUpload.js
import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../contexts/AuthContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useToast } from '../contexts/ToastContext';
import { createRoot } from 'react-dom/client';
import { useLanguage } from '../contexts/LanguageContext';
import { SanctionsReportStandalone } from './SanctionsReport';
import { generatePDF, calculateExpiryDate, formatDate } from '../utils/pdfGenerator';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Button from
 './ui/Button';
import { 
  submitBatchScreeningJob, 
  subscribeToJobUpdates, 
  getJobResults,
  getUserJobs,
  getJobStatus,
  getJobChain // <-- Import getJobChain
} from '../services/batchScreeningService';
import ContinuationChainTable from './ContinuationChainTable';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { getActiveSanctionsCount } from '../services/searchService';
import { getUsageStats } from '../services/usageService';
import SearchLimitWarning from './SearchLimitWarning';

// Batch job constants
const MAX_BATCH_SIZE = 250; // Maximum number of names per batch job

const MassUpload = () => {
  const t = useTranslation();
  const { currentUser, currentOrg } = useAuth();
  const { error, handleError: originalHandleError, clearError } = useErrorHandler();
  const { showError, showSuccess, showWarning } = useToast();
  
  // Enhanced error handler that refreshes usage stats on limit errors
  const handleError = async (error, context) => {
    // If it's a search limit error, refresh usage stats and show toast
    if (error?.code === 'SEARCH_LIMIT_EXCEEDED' && currentOrg?.id) {
      try {
        const freshStats = await getUsageStats(currentOrg.id);
        setUsageStats(freshStats);
      } catch (refreshError) {
        console.error('Error refreshing usage stats after limit error:', refreshError);
      }
      
      // Show toast notification instead of generic error
      const limitInfo = error.limitInfo;
      if (limitInfo) {
        showError(
          `Batch limit exceeded! You have used ${limitInfo.used} of ${limitInfo.totalLimit} searches this month. This batch requires ${limitInfo.required} searches.`,
          6000 // Show for 6 seconds
        );
        // Don't show generic error for limit exceeded
        return;
      }
    }
    
    // Call the original error handler for non-limit errors
    originalHandleError(error, context);
  };
  const { language } = useLanguage();
  const fileInputRef = useRef(null);
  const hasShownJobErrorRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Add this ref for debouncing fetchResults calls
  const fetchResultsTimeout = useRef(null);
  
  // Jobs list view state
  const [view, setView] = useState(searchParams.get('jobId') ? 'detail' : 'list');
  const [userJobs, setUserJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
  // States for file upload and name data
  const [uploadedData, setUploadedData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [totalUploadedCount, setTotalUploadedCount] = useState(0);
  
  // States for backend job management
  const [jobId, setJobId] = useState(searchParams.get('jobId') || null);
  const [jobStatus, setJobStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(!!searchParams.get('jobId'));
  const [completedCount, setCompletedCount] = useState(0);
  const [showSummary, setShowSummary] = useState(searchParams.get('summary') === 'true');
  const [hasStartedProcessing, setHasStartedProcessing] = useState(!!searchParams.get('jobId'));
  const [unsubscribeFunction, setUnsubscribeFunction] = useState(null);
  
  // New state to track which row is expanded to show detailed matches
  const [expandedRowId, setExpandedRowId] = useState(null);
  
  // New state to track previously processing names to prevent status flicker
  const [previouslyProcessingNames, setPreviouslyProcessingNames] = useState([]);
  
  // Stats for summary
  const [summaryStats, setSummaryStats] = useState({
    totalProcessed: parseInt(searchParams.get('totalProcessed') || '0'),
    totalWithMatches: parseInt(searchParams.get('totalWithMatches') || '0'),
    totalNoMatches: parseInt(searchParams.get('totalNoMatches') || '0'),
    totalMatches: parseInt(searchParams.get('totalMatches') || '0'),
  });

  // New state to track which items are being monitored
  const [monitoredItems, setMonitoredItems] = useState({});
  const [isAddingToMonitoring, setIsAddingToMonitoring] = useState(false);
  const [isLoadingConsolidatedResults, setIsLoadingConsolidatedResults] = useState(false);
  
  // Pagination state
  const [jobsPerPage, setJobsPerPage] = useState(10);
  const [totalJobsFetched, setTotalJobsFetched] = useState(0);
  const [hasMoreJobs, setHasMoreJobs] = useState(true);
  const [isLoadingMoreJobs, setIsLoadingMoreJobs] = useState(false);
  
  // UI state
  const [showPII, setShowPII] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Usage stats for limit checking
  const [usageStats, setUsageStats] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  
  // Load usage stats when organization changes
  useEffect(() => {
    const loadUsageStats = async () => {
      if (!currentOrg?.id) {
        setUsageStats(null);
        return;
      }
      
      try {
        setUsageLoading(true);
        const stats = await getUsageStats(currentOrg.id);
        setUsageStats(stats);
      } catch (error) {
        console.error('Error loading usage stats:', error);
        setUsageStats(null);
      } finally {
        setUsageLoading(false);
      }
    };

    loadUsageStats();
  }, [currentOrg?.id]);

  // Check if batch size would exceed limits for existing batches (not fresh uploads)
  // This handles cases where usage stats change after a batch is already loaded
  useEffect(() => {
    if (
      view === 'detail' && 
      uploadedData.length > 0 && 
      usageStats && 
      usageStats.allowed && // Not at limit yet
      uploadedData.length > (usageStats.remaining || 0) && // But batch would exceed
      !isProcessing && 
      !jobId &&
      !isUploading // Don't trigger for fresh uploads (handled in handleFileUpload)
    ) {
      const remaining = usageStats.remaining || 0;
      const batchSize = uploadedData.length;
      
      showWarning(
        `Batch no longer valid! Other users have consumed searches. This batch contains ${batchSize} names, but you now only have ${remaining} searches remaining. Please reduce your batch size or upgrade your plan.`,
        8000 // Show for 8 seconds
      );
      
      // Redirect back to main list after a short delay
      setTimeout(() => {
        goBackToList();
      }, 2000);
    }
  }, [view, uploadedData.length, usageStats, isProcessing, jobId, isUploading, showWarning]);

  // Load saved data on initial render if jobId exists
  useEffect(() => {
    const savedJobId = searchParams.get('jobId');
    if (savedJobId) {
      setJobId(savedJobId);
      setView('detail');
      
      // Force isProcessing to true to prevent empty state from showing
      // until we get actual job status
      setIsProcessing(true);
      setHasStartedProcessing(true);
      
      // Try to load from localStorage
      const savedDataJson = localStorage.getItem(`massUpload_${savedJobId}`);
      if (savedDataJson) {
        try {
          const savedData = JSON.parse(savedDataJson);
          // Set data from localStorage temporarily
          setUploadedData(savedData.uploadedData || []);
          setTotalUploadedCount(savedData.totalUploadedCount || 0);
          setCompletedCount(savedData.completedCount || 0);
          
          // First check if this is a chain job
          getJobStatus(savedJobId).then(jobData => {
            if (jobData.continuationChain || jobData.rootJobId) {
              console.log("Detected job chain, fetching complete chain data");
              // This is part of a chain - get the complete chain
              getJobChain(savedJobId).then(jobChain => {
                if (jobChain && jobChain.length > 0) {
                  // Find the root job and latest job
                  const rootJob = jobChain.find(job => !job.previousJobInChain);
                  const rootJobId = rootJob?.id || savedJobId;
                  const latestJobId = getLatestJobInChain(jobChain);
                  
                  // Update job status with chain info
                  setJobStatus({
                    ...jobData,
                    continuationChain: jobChain,
                    isContinuationChain: true
                  });
                  
                  // Fetch consolidated results with forceRefresh to get all names
                  fetchConsolidatedResults(rootJobId, jobChain.map(job => job.id));
                  
                  // Subscribe to the latest job
                  subscribeToJob(latestJobId, rootJobId);
                } else {
                  // No chain found, fallback to standard handling
                  fetchResults(savedJobId, true); // Force refresh to get all names
          subscribeToJob(savedJobId);
                }
              }).catch(error => {
                console.error("Error getting job chain:", error);
                // Fallback to standard job handling
                fetchResults(savedJobId, true);
                subscribeToJob(savedJobId);
              });
            } else {
              // Standard job - get fresh data with all names
              fetchResults(savedJobId, true); // Force refresh to get all names
              subscribeToJob(savedJobId);
            }
          }).catch(error => {
            console.error("Error checking job type:", error);
            // Just proceed with subscription as fallback
            subscribeToJob(savedJobId);
          });
        } catch (e) {
          console.error("Error parsing saved data", e);
          // If we can't parse the saved data, just fetch fresh data and subscribe
          fetchResults(savedJobId, true);
          subscribeToJob(savedJobId);
        }
      } else {
        console.log("No saved data, getting fresh data for:", savedJobId);
        // No saved data - fetch fresh data with all names
        fetchResults(savedJobId, true);
        // Subscribe to job updates
        subscribeToJob(savedJobId);
      }
    } else {
      // If no job ID, fetch the user's jobs
      fetchUserJobs();
    }
  }, []);

  // Save important state to localStorage when it changes
  useEffect(() => {
    if (jobId) {
      const dataToSave = {
        uploadedData,
        totalUploadedCount,
        completedCount,
        summaryStats
      };
      localStorage.setItem(`massUpload_${jobId}`, JSON.stringify(dataToSave));
      
      // Update URL params
      setSearchParams({
        jobId,
        summary: showSummary.toString(),
        totalProcessed: summaryStats.totalProcessed.toString(),
        totalWithMatches: summaryStats.totalWithMatches.toString(),
        totalNoMatches: summaryStats.totalNoMatches.toString(),
        totalMatches: summaryStats.totalMatches.toString()
      });
    }
  }, [jobId, uploadedData, completedCount, showSummary, summaryStats]);

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    clearError();
    
    // Check file size - prevent extremely large files
    if (file.size > 10000000) { // 10MB limit
      handleError(new Error('File size exceeds the 10MB limit'), { component: 'MassUpload', action: 'fileUpload' });
      setIsUploading(false);
      return;
    }
    
    // Check file type
    const isCSVByMime = file.type === 'text/csv';
    const isCSVByExtension = file.name.toLowerCase().endsWith('.csv');
    const isTextPlain = file.type === 'text/plain';
    
    if (!isCSVByMime && !isCSVByExtension && !isTextPlain) {
      handleError(new Error('Please upload a CSV file or text file with names.'), { component: 'MassUpload', action: 'fileUpload' });
      setIsUploading(false);
      return;
    }
    
    console.log("Processing file:", file.name, "Size:", file.size, "Type:", file.type);
    
    // Simple line-by-line parsing for single column files
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim() !== '');
        
        if (lines.length === 0) {
          handleError(new Error('File appears to be empty'), { component: 'MassUpload', action: 'fileUpload' });
          setIsUploading(false);
          return;
        }
        
        // Determine if we should skip the first row (header)
        const firstRow = lines[0].trim();
        const looksLikeHeader = firstRow.toLowerCase().includes('name') || 
                                firstRow.includes('_') || 
                                firstRow.includes(':') ||
                                firstRow.length < 3;
        
        const startIndex = looksLikeHeader ? 1 : 0;
        console.log("First row:", firstRow, "Treating as header:", looksLikeHeader);
        
        // Parse all lines (skipping header if necessary)
        const data = lines.slice(startIndex).map((line, index) => {
          const name = line.trim();
          if (!name) return null;
          
          return {
            id: `name-${index}`,
            name: name,
            status: 'pending',
            matches: 0,
            verificationId: null,
            order: index // Add order property to track original position
          };
        }).filter(Boolean); // Remove null entries (empty names)
        
        if (data.length === 0) {
          handleError(new Error('No valid names found in the file.'), { component: 'MassUpload', action: 'fileUpload' });
          setIsUploading(false);
          return;
        }
        
        // Check if the number of names exceeds the limit
        if (data.length > MAX_BATCH_SIZE) {
          // Create a custom error with properties that will be preserved
          const batchSizeError = new Error('Batch size limit exceeded');
          batchSizeError.code = 'batch-size-limit-exceeded';
          batchSizeError.nameCount = data.length;
          
          // Pass this error to the error handler
          handleError(batchSizeError, { component: 'MassUpload', action: 'fileUpload' });
          setIsUploading(false);
          return;
        }
        
        console.log(`Processed ${data.length} names from file`);
        
        // Do a real-time usage check after file upload to handle multi-user scenarios
        const checkUsageAfterUpload = async () => {
          if (currentOrg?.id) {
            try {
              const realTimeStats = await getUsageStats(currentOrg.id);
              setUsageStats(realTimeStats); // Update cached stats
              
              // Check if the batch would exceed current limits
              const remaining = realTimeStats.remaining || 0;
              const batchSize = data.length;
              
                             if (!realTimeStats.allowed) {
                 // Account is at limit
                 showWarning(
                   `Search limit reached! You have used ${realTimeStats.used} of ${realTimeStats.totalLimit} searches this month. Cannot process any batch uploads.`,
                   6000
                 );
                 
                 // Redirect back to main list after a short delay
                 setTimeout(() => {
                   goBackToList();
                 }, 2000);
                 
                 setIsUploading(false);
                 return false;
               }
               
               if (batchSize > remaining) {
                 // Batch would exceed limits
                 showWarning(
                   `Batch size too large! This batch contains ${batchSize} names, but you only have ${remaining} searches remaining this month. Please reduce your batch size or upgrade your plan.`,
                   8000
                 );
                 
                 // Redirect back to main list after a short delay
                 setTimeout(() => {
                   goBackToList();
                 }, 2000);
                 
                 setIsUploading(false);
                 return false;
               }
              
              return true;
            } catch (error) {
              console.error('Error checking real-time usage after upload:', error);
              // Continue with cached stats if real-time check fails
              return true;
            }
          }
          return true;
        };
        
                 // Check usage before proceeding
         checkUsageAfterUpload().then((canProceed) => {
           if (canProceed) {
             setUploadedData(data);
             setTotalUploadedCount(data.length);
             setShowSummary(false);
             setHasStartedProcessing(false);
             setCompletedCount(0);
             
             // Reset job information
             setJobId(null);
             setJobStatus(null);
             setSearchParams({}); // Clear URL params
             
             // Reset summary stats
             setSummaryStats({
               totalProcessed: 0,
               totalWithMatches: 0,
               totalNoMatches: 0,
               totalMatches: 0,
             });
             
             // Unsubscribe from any previous job
             if (unsubscribeFunction) {
               unsubscribeFunction();
               setUnsubscribeFunction(null);
             }
             
             // Clear localStorage for any previous job
             if (jobId) {
               localStorage.removeItem(`massUpload_${jobId}`);
             }
           } else {
             // Don't set the uploaded data if usage check failed
             resetFileInput();
           }
           setIsUploading(false);
         });
      } catch (error) {
        console.error("Error parsing file:", error);
        handleError(new Error('Error parsing file: ' + error.message), { component: 'MassUpload', action: 'fileUpload' });
        setIsUploading(false);
      }
    };
    
    reader.onerror = (error) => {
      console.error("FileReader error:", error);
      handleError(new Error('Error reading file: ' + (error ? error.message : 'Unknown error')), { component: 'MassUpload', action: 'fileUpload' });
      setIsUploading(false);
    };
    
    reader.readAsText(file);
  };

  // Reset the file input
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Clear all data and reset the component
  const handleClearAll = () => {
    // Unsubscribe from any active subscription
    if (unsubscribeFunction) {
      unsubscribeFunction();
      setUnsubscribeFunction(null);
    }
    
    // Clear any job-specific state
    setJobId(null);
    setJobStatus(null);
    setUploadedData([]);
    setIsProcessing(false);
    setCompletedCount(0);
    setTotalUploadedCount(0);
    setShowSummary(false);
    setHasStartedProcessing(false);
    
    // Clean up localStorage
    if (jobId) {
      localStorage.removeItem(`massUpload_${jobId}`);
    }
    
    // Update view and URL
    setView('list');
    setSearchParams({}); // Clear URL params
    
    resetFileInput();
    clearError();
    
    // Refresh the jobs list
    fetchUserJobs();
  };

  // Go back to jobs list view
  const goBackToList = () => {
    console.log("Going back to jobs list, cleaning up subscription");
    
    // Unsubscribe from current job updates
    if (unsubscribeFunction) {
      unsubscribeFunction();
      setUnsubscribeFunction(null);
    }
    
    // Clear any job-specific state
    setJobId(null);
    setJobStatus(null);
    setUploadedData([]);
    setCompletedCount(0);
    setTotalUploadedCount(0);
    setShowSummary(false);
    
    // Update view and URL
    setView('list');
    setSearchParams({});
    
    // Refresh the jobs list
    fetchUserJobs();
  };

  // CHANGE 2: Simplify the subscription update function
  const subscribeToJob = (jobId, rootJobId = null) => {
    // Unsubscribe from any previous subscription
    if (unsubscribeFunction) {
      unsubscribeFunction();
      setUnsubscribeFunction(null);
    }
    
    console.log("Setting up subscription for job:", jobId);
    
    const unsubscribe = subscribeToJobUpdates(jobId, (updatedJobData, error) => {
      if (error) {
        console.error("Error in job subscription:", error);
        handleError(error, { component: 'MassUpload', action: 'subscribeToJobUpdates' });
        return;
      }
      
      if (!updatedJobData) {
        console.log("Job not found or deleted");
        showError(t('errors.jobNotFound') || 'The requested job was not found.');
        handleClearAll();
        return;
      }
      
      // Update job status
      console.log("Received job update:", updatedJobData.status);
      
      // Keep track of whether this is a job chain
      const isChainJob = rootJobId !== null || updatedJobData.continuationChain || updatedJobData.rootJobId;
      
      // Update job status - preserve continuationChain info if it exists
      setJobStatus(prevStatus => ({
        ...updatedJobData,
        continuationChain: prevStatus?.continuationChain || updatedJobData.continuationChain,
        isContinuationChain: prevStatus?.isContinuationChain || updatedJobData.isContinuationChain,
        rootJobId: prevStatus?.rootJobId || updatedJobData.rootJobId || rootJobId
      }));
      
      // Detect status transitions for UI updates
      if (jobStatus?.status !== updatedJobData.status) {
        console.log(`Job status transition: ${jobStatus?.status || 'unknown'} -> ${updatedJobData.status}`);
        
        // For stopping/stopped status transitions, immediately refresh results
        if (updatedJobData.status === 'stopping' || updatedJobData.status === 'stopped') {
          console.log(`Job ${updatedJobData.status}, refreshing results immediately`);
          fetchResults(jobId, true);
        }
      }
      
      // Handle job status changes
      if (updatedJobData.status === 'processing') {
        setIsProcessing(true);
        
        // Update progress information if available
        if (updatedJobData.progress) {
          setCompletedCount(updatedJobData.progress.completed || 0);
        }
        
        // Update processing status for any names currently being processed
        if (updatedJobData.processingNames && updatedJobData.processingNames.length > 0) {
          const processingNames = updatedJobData.processingNames;
          
          // Only update status - don't add or remove records
          setUploadedData(prevData => {
            return prevData.map(item => {
              // If this name is being processed, update its status
              if (processingNames.includes(item.name)) {
                return { ...item, status: 'processing' };
              }
              
              // For other items, keep them as-is (unless they were processing before)
              if (item.status === 'processing' && !processingNames.includes(item.name)) {
                // Will be updated in fetchResults with actual status soon
                return item;
              }
              
              return item;
            });
          });
          
          // Track which names we've seen processing
          setPreviouslyProcessingNames(processingNames);
        }
        
        // Fetch completed results if we have some
        if (updatedJobData.progress && updatedJobData.progress.completed > 0) {
          // Debounce the fetchResults call to minimize backend calls
          if (fetchResultsTimeout.current) {
            clearTimeout(fetchResultsTimeout.current);
          }
          
          fetchResultsTimeout.current = setTimeout(() => {
            // For chain jobs, use fetchConsolidatedResults instead of fetchResults
            if (isChainJob) {
              // Refresh the chain data to ensure we have the latest
              getJobChain(rootJobId || jobId).then(jobChain => {
                if (jobChain && jobChain.length > 0) {
                  fetchConsolidatedResults(rootJobId || jobId, jobChain.map(job => job.id));
                }
              }).catch(error => {
                console.error("Error refreshing job chain:", error);
              });
            } else {
              fetchResults(jobId);
            }
            fetchResultsTimeout.current = null;
          }, 1000);
        }
      }
      // Handle stopping and stopped status - update processing items to completed or pending
      else if (updatedJobData.status === 'stopping' || updatedJobData.status === 'stopped') {
        // If job is stopping or stopped, set processing state accordingly
        setIsProcessing(updatedJobData.status === 'stopping');
        
        // Get completed and processing items from the job data
        const completedNames = updatedJobData.completedNames || [];
        const processingNames = updatedJobData.processingNames || [];
        
        // Update status of all items based on job's state
        setUploadedData(prevData => {
          return prevData.map(item => {
            // If the item was previously "processing" but job is now stopped,
            // update its status based on whether it appears in completedNames
            if (item.status === 'processing') {
              if (completedNames.includes(item.name)) {
                // This item was completed before stopping
                return { 
                  ...item, 
                  status: 'completed',
                  matches: 0  // Default to 0 matches if we don't have result data
                };
              } else {
                // This item was not completed, revert to pending
                return { ...item, status: 'pending' };
              }
            }
            // Keep other items as they are
            return item;
          });
        });
        
        // Force a refresh to get the latest results for any completed items
        if (updatedJobData.status === 'stopped') {
          fetchResults(jobId, true);
        }
      }
      // Handle job that has become continued (rolled over to next job)
      else if (updatedJobData.status === 'continued') {
        console.log("Job continued - transitioning to next job in chain");
        
        // If we have continuationJobId, switch subscription to that job
        if (updatedJobData.continuationJobId || updatedJobData.nextJobInChain) {
          const nextJobId = updatedJobData.continuationJobId || updatedJobData.nextJobInChain;
          console.log("Switching subscription to continuation job:", nextJobId);
          
          // Determine the root job ID (might be this job or its rootJobId)
          const actualRootJobId = updatedJobData.rootJobId || rootJobId || jobId;
          
          // Fetch the job chain to get fresh data
          getJobChain(actualRootJobId).then(jobChain => {
            if (jobChain && jobChain.length > 0) {
              console.log("Got updated job chain with", jobChain.length, "jobs");
              
              // Update job status with the chain information
              setJobStatus(prevStatus => ({
                ...updatedJobData,
                continuationChain: jobChain,
                isContinuationChain: true
              }));
              
              // Fetch consolidated results to update with latest data
              fetchConsolidatedResults(actualRootJobId, jobChain.map(job => job.id));
              
              // Subscribe to the new job
              setTimeout(() => {
                subscribeToJob(nextJobId, actualRootJobId);
              }, 100);
            } else {
              // If we can't get the chain, just subscribe to the next job directly
              console.log("No job chain found, directly subscribing to next job");
              setTimeout(() => {
                subscribeToJob(nextJobId, actualRootJobId);
              }, 100);
            }
          }).catch(error => {
            console.error("Error getting job chain after continuation:", error);
            // Subscribe to next job anyway as fallback
            setTimeout(() => {
              subscribeToJob(nextJobId, actualRootJobId);
            }, 100);
          });
        } else {
          console.warn("Job continued but no continuation job ID found");
          // Update job status to show it's continued but we can't follow
          setIsProcessing(false);
        }
      }
      // Special handling for continuation chains
      else if (updatedJobData.continuationChain && updatedJobData.continuationChain.length > 0) {
        // If any job in the chain is processing, set overall status to processing
        const isAnyProcessing = updatedJobData.continuationChain.some(job => job.status === 'processing');
        setIsProcessing(isAnyProcessing);
        
        // If a job in the chain completes, fetch consolidated results
        if (updatedJobData.status === 'completed' || 
            updatedJobData.status === 'completed-with-matches' ||
            updatedJobData.status === 'failed' ||
            updatedJobData.status === 'error') {
          
          // Get all job IDs from the chain
          const chainJobIds = updatedJobData.continuationChain.map(job => job.id);
          
          // Fetch consolidated results with debouncing
          if (fetchResultsTimeout.current) {
            clearTimeout(fetchResultsTimeout.current);
          }
          
          fetchResultsTimeout.current = setTimeout(() => {
            fetchConsolidatedResults(rootJobId || jobId, chainJobIds);
            fetchResultsTimeout.current = null;
          }, 1000);
        }
      }
      // If job completed and it's NOT a chain job, fetch final results
      else if (
        !isChainJob && 
        (updatedJobData.status === 'completed' || 
        updatedJobData.status === 'completed-with-matches' ||
        updatedJobData.status === 'failed')
      ) {
        setIsProcessing(false);
        fetchResults(jobId);
      }
      // If job completed and it IS a chain job, don't fetch regular results - we already have consolidated data
      else if (
        isChainJob && 
        (updatedJobData.status === 'completed' || 
        updatedJobData.status === 'completed-with-matches' ||
        updatedJobData.status === 'failed')
      ) {
        setIsProcessing(false);
        // Don't call fetchResults - it would overwrite our consolidated data
        console.log("Chain job completed - keeping consolidated results");
      }
    });
    
    setUnsubscribeFunction(() => unsubscribe);
  };

  // CHANGE 4: Add helper function for updating summary stats
  const updateSummaryStats = (results) => {
    if (!results || results.length === 0) return;
    
        const completedItems = results.filter(result => 
          result.status === 'completed' || 
          result.status === 'completed-with-matches' || 
          result.status === 'error'
        ).length;
        
          setCompletedCount(completedItems);
        
        const withMatches = results.filter(r => (r.matches || 0) > 0).length;
        const withoutMatches = results.filter(r => 
          (r.status === 'completed' || r.status === 'completed-with-matches') && 
          (r.matches || 0) === 0
        ).length;
        const totalMatches = results.reduce((sum, r) => sum + (r.matches || 0), 0);
        
        setSummaryStats({
          totalProcessed: completedItems,
          totalWithMatches: withMatches,
          totalNoMatches: withoutMatches,
          totalMatches: totalMatches,
        });
  };

  // CHANGE 1: Modify fetchResults to be the single source of truth for data
  const fetchResults = async (jobId, forceRefresh = false) => {
    try {
      // First get the job status to get the full list of names
      const jobData = await getJobStatus(jobId);
      const results = await getJobResults(jobId);
      
      if (!jobData) return;
      
      // Special handling for stopped jobs - make sure to get all results
      const isStopped = jobData.status === 'stopped';
      
      // Reconstruct full data only if we don't have it yet or force refresh is set
      if (uploadedData.length === 0 || forceRefresh) {
        console.log("Setting initial data from job status and results");
        
        // Get all names from the job document
        let allNames = [];
        
        // Get names from different possible sources in job data
        if (jobData.names && Array.isArray(jobData.names)) {
          console.log("Found names array in job data with", jobData.names.length, "names");
          allNames = [...jobData.names];
        } else if (jobData.pendingNames && jobData.completedNames) {
          // Combine pending and completed names
          console.log("Combining pending and completed names from job data");
          allNames = [...(jobData.pendingNames || []), ...(jobData.completedNames || [])];
        } else if (jobData.size || jobData.progress?.total) {
          // If we only have a count but no names, use the results we have
          // and create placeholder entries for missing items
          const size = jobData.size || jobData.progress?.total || 0;
          console.log("Creating placeholders based on job size:", size);
          
          // Create a set of known names from results
          const knownNames = new Set();
          if (results && results.length > 0) {
            results.forEach(result => knownNames.add(result.name));
          }
          
          // Create placeholders for the difference
          const placeholderCount = Math.max(0, size - knownNames.size);
          if (placeholderCount > 0) {
            console.log(`Creating ${placeholderCount} placeholders for missing names`);
            for (let i = 0; i < placeholderCount; i++) {
              allNames.push(`Pending Item ${i+1}`);
            }
          }
          
          // Add known names from results
          knownNames.forEach(name => allNames.push(name));
        }
        
        // Create a results lookup map
        const resultsMap = new Map();
        if (results && results.length > 0) {
          results.forEach(result => {
            resultsMap.set(result.name, result);
          });
        }
        
        // Create the processing names set
        const processingNames = new Set(jobData.processingNames || []);
        
        // Create a set of completed names
        const completedNames = new Set(jobData.completedNames || []);
        
        // Create the reconstructed data array
        const reconstructedData = allNames.map((name, index) => {
          // Look for matching result
          const matchingResult = resultsMap.get(name);
          
          if (matchingResult) {
            // If we have a result, use its data
            return {
              id: `name-${index}`,
              name: name,
              status: matchingResult.status || 'pending',
              matches: matchingResult.matches || 0,
              verificationId: matchingResult.verificationId,
              searchId: matchingResult.searchId,
              detailedMatches: matchingResult.details || [],
              order: index
            };
          } else {
            // No result yet - determine if it's pending or processing
            // For stopped jobs, if the name is in completedNames but we have no result,
            // mark it as completed with 0 matches
            if (isStopped && completedNames.has(name)) {
              return {
                id: `name-${index}`,
                name: name,
                status: 'completed',
                matches: 0,
                verificationId: null,
                searchId: null,
                detailedMatches: [],
                order: index
              };
            } else {
              return {
                id: `name-${index}`,
                name: name,
                // Use processing status if in the processing list
                status: processingNames.has(name) ? 'processing' : 'pending',
                matches: 0,
                verificationId: null,
                searchId: null,
                detailedMatches: [],
                order: index
              };
            }
          }
        });
        
        setUploadedData(reconstructedData);
        setTotalUploadedCount(reconstructedData.length);
        setHasStartedProcessing(true);
      } else {
        // Only update existing records, never add or remove
        setUploadedData(prevData => {
          return prevData.map(item => {
            // Find matching result by name
            const matchingResult = results?.find(r => r.name === item.name);
            
            // If no match, check if it's in the processing list or completed (for stopped jobs)
            if (!matchingResult) {
              const processingNames = jobData.processingNames || [];
              const completedNames = jobData.completedNames || [];
              
              // For stopped jobs, if an item was in processing status but the job is now stopped
              if (isStopped && item.status === 'processing') {
                if (completedNames.includes(item.name)) {
                  return { ...item, status: 'completed', matches: 0 };
                } else {
                  return { ...item, status: 'pending' };
                }
              }
              
              // Regular processing status update for active jobs
              if (processingNames.includes(item.name)) {
                return { ...item, status: 'processing' };
              }
              return item;
            }
            
            // Update the matching item with result data
            return {
              ...item,
              status: matchingResult.status || item.status,
              matches: matchingResult.matches || 0,
              verificationId: matchingResult.verificationId,
              searchId: matchingResult.searchId,
              detailedMatches: matchingResult.details || []
            };
          });
        });
      }
      
      // Update progress and summary stats from job data and results
      if (jobData.progress) {
        setCompletedCount(jobData.progress.completed || 0);
      }
      
      // Update summary stats based on all results
      if (results && results.length > 0) {
        updateSummaryStats(results);
      }
      
    } catch (error) {
      console.error("Error fetching results:", error);
      handleError(error, { component: 'MassUpload', action: 'fetchResults' });
    }
  };

  // Start the screening process by submitting to backend
  const handleStartScreening = async () => {
    if (isProcessing || uploadedData.length === 0) return;
    
    // Do a real-time limit check before starting the batch to handle multi-user scenarios
    let realTimeStats = null;
    if (currentOrg?.id) {
      try {
        realTimeStats = await getUsageStats(currentOrg.id);
        setUsageStats(realTimeStats); // Update cached stats
        
        const remaining = realTimeStats.remaining || 0;
        const batchSize = uploadedData.length;
        
        if (!realTimeStats.allowed || remaining < batchSize) {
          const limitError = new Error(`Insufficient search capacity. This batch requires ${batchSize} searches, but you only have ${remaining} searches remaining this month.`);
          limitError.code = 'SEARCH_LIMIT_EXCEEDED';
          limitError.limitInfo = {
            ...realTimeStats,
            required: batchSize,
            remaining: remaining
          };
          handleError(limitError, { component: 'MassUpload', action: 'startScreening' });
          return;
        }
      } catch (limitCheckError) {
        console.error('Error checking real-time limits for batch:', limitCheckError);
        // Fall back to cached check if real-time check fails
        if (usageStats && currentOrg?.id) {
          const remaining = usageStats.remaining || 0;
          const batchSize = uploadedData.length;
          
          if (remaining < batchSize) {
            const limitError = new Error(`Insufficient search capacity. This batch requires ${batchSize} searches, but you only have ${remaining} searches remaining this month.`);
            limitError.code = 'SEARCH_LIMIT_EXCEEDED';
            limitError.limitInfo = {
              ...usageStats,
              required: batchSize,
              remaining: remaining
            };
            handleError(limitError, { component: 'MassUpload', action: 'startScreening' });
            return;
          }
        }
      }
    }
    
    // Double-check batch size limit
    if (uploadedData.length > MAX_BATCH_SIZE) {
      // Create a custom error with properties that will be preserved
      const batchSizeError = new Error('Batch size limit exceeded');
      batchSizeError.code = 'batch-size-limit-exceeded';
      batchSizeError.nameCount = uploadedData.length;
      
      // Pass this error to the error handler
      handleError(batchSizeError, { component: 'MassUpload', action: 'startScreening' });
      return;
    }

    setIsProcessing(true);
    clearError();

    try {
      // Check if the user already has an active job using our helper
      const activeJobCheck = await checkActiveJobs();
      
      if (activeJobCheck.hasActiveJobs) {
        // If there's an active job, throw an error with a simpler message and custom code
        const activeJobError = new Error('You already have an active batch job. Please wait for it to complete before starting a new one.');
        activeJobError.code = 'active-job-exists';
        activeJobError.activeJobId = activeJobCheck.activeJobId; // Include job ID for console logs only
        
        // Explicit console log for debugging
        console.log(`Blocking new job creation - Active job ID: ${activeJobCheck.activeJobId}`, activeJobCheck);
        
        throw activeJobError;
      }
      
      setHasStartedProcessing(true);
      setShowSummary(false);

      // Extract just the names from the uploaded data
      const names = uploadedData.map(item => item.name);
      
      // Submit the batch job to the backend
      console.log("Submitting batch job with", names.length, "names");
      const newJobId = await submitBatchScreeningJob(names, currentUser?.uid, currentOrg?.id);
      console.log("Job submitted with ID:", newJobId);
      
      setJobId(newJobId);
      
      // Set all names to pending status to start
      setUploadedData(prevData => 
        prevData.map(item => ({
          ...item,
          status: 'pending'
        }))
      );
      
      // Subscribe to job updates - will handle all status changes
      subscribeToJob(newJobId);
      
      // Update URL params
      setSearchParams({
        jobId: newJobId
      });
      
      // Refresh usage stats after starting the job
      try {
        const freshStats = await getUsageStats(currentOrg.id);
        setUsageStats(freshStats);
      } catch (error) {
        console.error('Error refreshing usage stats after job start:', error);
      }
      
    } catch (error) {
      console.error("Error submitting batch job:", error);
      handleError(error, { component: 'MassUpload', action: 'startScreening' });
      setIsProcessing(false);
      setHasStartedProcessing(false);
    }
  };
  
  // Check for active jobs when component loads in detail view or when a new file is uploaded
  useEffect(() => {
    if (view === 'detail' && uploadedData.length > 0 && !isProcessing && !jobId) {
      // Check for active jobs
      const checkForActiveJobs = async () => {
        try {
          const activeJobCheck = await checkActiveJobs();
          
          if (activeJobCheck.hasActiveJobs) {
            // Show a warning to the user without the job ID
            const activeJobError = new Error('You already have an active batch job. Please wait for it to complete before starting a new screening.');
            activeJobError.code = 'active-job-exists';
            activeJobError.activeJobId = activeJobCheck.activeJobId; // Include job ID for console logs only
            
            // Explicit console log for debugging
            console.log(`Proactive check found active job - Active job ID: ${activeJobCheck.activeJobId}`, activeJobCheck);
            
            handleError(activeJobError, { component: 'MassUpload', action: 'checkActiveJobs' });
          }
        } catch (error) {
          console.error("Error checking active jobs:", error);
        }
      };
      
      checkForActiveJobs();
    }
  }, [view, uploadedData.length, isProcessing, jobId, currentUser]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Unsubscribe from job updates when component unmounts
      if (unsubscribeFunction) {
        unsubscribeFunction();
      }
    };
  }, [unsubscribeFunction]);

  // Get status class for styling
  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'text-gray-500 dark:text-gray-400';
      case 'processing': return 'text-blue-600 dark:text-blue-400';
      case 'stopping': return 'text-orange-600 dark:text-orange-400';
      case 'stopped': return 'text-red-600 dark:text-red-400';
      case 'completed': return 'text-green-600 dark:text-green-400';
      case 'completed-with-matches': return 'text-yellow-600 dark:text-yellow-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Get status icon based on status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <i className="fa-solid fa-clock"></i>;
      case 'processing': return <i className="fa-solid fa-spinner fa-spin"></i>;
      case 'stopping': return <i className="fa-solid fa-hand-stop fa-pulse"></i>;
      case 'stopped': return <i className="fa-solid fa-stop-circle"></i>;
      case 'continued': return <i className="fa-solid fa-link"></i>;
      case 'completed': return <i className="fa-solid fa-check-circle"></i>;
      case 'completed-with-matches': return <i className="fa-solid fa-triangle-exclamation"></i>;
      case 'error': return <i className="fa-solid fa-times-circle"></i>;
      default: return null;
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return t('massUpload.pending');
      case 'processing': return t('massUpload.processing');
      case 'stopping': return t('massUpload.stopping') || 'Stopping';
      case 'stopped': return t('massUpload.stopped') || 'Stopped';
      case 'completed': return t('massUpload.completed');
      case 'continued': return t('massUpload.continued') || 'Continued';
      case 'completed-with-matches': return t('massUpload.foundMatches');
      case 'completed-with-errors': return t('massUpload.completedWithErrors');
      case 'error': return t('massUpload.error');
      default: return '';
    }
  };

  // Calculate progress percentage
  const calculateProgress = () => {
    if (totalUploadedCount === 0) return 0;
    // Ensure the progress can't go above 100%
    return Math.min(100, Math.round((completedCount / totalUploadedCount) * 100));
  };

  // New function to toggle row expansion
  const toggleRowExpansion = (id) => {
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
    }
  };

  // Function to view full report
  const viewFullReport = (verificationId, searchId, name) => {
    if (!verificationId && !searchId) {
      showError(t('errors.noVerificationId') || 'No verification ID available for this match');
      return;
    }
    
    // Debug: Log what values we're using for navigation
    console.log("Navigating to verification report with:", {
      name,
      verificationId,
      searchId,
      navigatePath: `/verification/${verificationId}${searchId ? `?searchId=${searchId}` : ''}`
    });
    
    // For mass upload results, always use the verification route, not the search ID route
    console.log("Navigating to verification report for:", name, "ID:", verificationId);
    
    // Find the item in uploadedData to check if it has matches
    const item = uploadedData.find(item => item.verificationId === verificationId);
    console.log("Item data for verification:", item);
    
    // Always navigate to the verification path for mass upload results
    // Include searchId as a query parameter if available
    navigate(
      `/verification/${verificationId}${searchId ? `?searchId=${searchId}` : ''}`,
      { state: { from: 'report' } } // Add source info for back button
    );
  };

  // Function to download PDF report for clean searches
  const downloadPdfReport = async (verificationId, name) => {
    if (!verificationId) {
      showError(t('errors.noVerificationId') || 'No verification ID available for this report');
      return;
    }
    
    console.log("Generating PDF report for:", name, "ID:", verificationId);
    
    try {
      // Show loading state
      const loadingIndicator = document.createElement('div');
      loadingIndicator.id = 'pdf-loading-indicator';
      loadingIndicator.style.display = 'flex';
      loadingIndicator.style.position = 'fixed';
      loadingIndicator.style.top = '0';
      loadingIndicator.style.left = '0';
      loadingIndicator.style.right = '0';
      loadingIndicator.style.bottom = '0';
      loadingIndicator.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      loadingIndicator.style.zIndex = '9999';
      loadingIndicator.style.justifyContent = 'center';
      loadingIndicator.style.alignItems = 'center';
      loadingIndicator.style.color = 'white';
      loadingIndicator.style.fontSize = '18px';
      
      const loadingContent = document.createElement('div');
      loadingContent.style.display = 'flex';
      loadingContent.style.flexDirection = 'column';
      loadingContent.style.alignItems = 'center';
      
      const loadingIcon = document.createElement('i');
      loadingIcon.className = 'fa-solid fa-circle-notch fa-spin fa-2x';
      loadingIcon.style.marginBottom = '8px';
      
      const loadingText = document.createElement('span');
      loadingText.textContent = t('results.generatingPdfReport') || 'Generating PDF Report...';
      
      loadingContent.appendChild(loadingIcon);
      loadingContent.appendChild(loadingText);
      loadingIndicator.appendChild(loadingContent);
      document.body.appendChild(loadingIndicator);

      // Get total active sanctions count
      let totalSanctionsCount;
      try {
        totalSanctionsCount = await getActiveSanctionsCount();
      } catch (error) {
        console.error("Error fetching total sanctions count:", error);
        // Fallback to a reasonable value
        totalSanctionsCount = 6200;
      }
      
      // Find the corresponding item in the uploaded data
      const foundItem = uploadedData.find(i => i.verificationId === verificationId && i.name === name);
      
      // Determine if this is likely a company/organization based on name patterns
      // This helps ensure correct entity type display even without explicit type info
      const isLikelyCompany = foundItem?.type === 'enterprise' || 
                           /\b(LLC|Ltd|Inc|Corp|GmbH|Co\.|Company|Limited|Corporation|Group|Holdings|Technologies|Solutions|International|Enterprises|Partners|Associates)\b/i.test(name);
      
      // Prepare data for the report
      const searchDate = new Date();
      const searchTimeFormatted = formatDate(searchDate);
      const expiryDateFormatted = calculateExpiryDate(searchDate);
      
      // Use all three sources we know exist in the database
      const sources = ["Financial Sanctions", "Travel Bans", "UN Sanctions"];
      
      const reportData = {
        name: name,
        verificationId: verificationId,
        timestamp: searchTimeFormatted,
        recordsScanned: totalSanctionsCount, // Use actual count instead of hardcoded value
        sources: sources,
        expiryDate: expiryDateFormatted,
        issuedBy: "Protego",
        entityType: foundItem?.type || (isLikelyCompany ? 'enterprise' : 'person'),
        runBy: currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
        runByEmail: currentUser?.email || ''
      };
      
      // Create a temporary div for rendering the report
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = '800px'; // Best width for A4 format
      tempDiv.style.height = '1120px'; // Best height for A4 format (approximately)
      document.body.appendChild(tempDiv);
      
      // Create a root using the client API
      const tempRoot = createRoot(tempDiv);
      
      // Create a promise to handle the rendering and PDF generation
      await new Promise((resolve) => {
        // Use the standalone version that doesn't depend on React hooks
        tempRoot.render(<SanctionsReportStandalone data={reportData} language={language} />);
        
        // Allow time for the report to render
        setTimeout(async () => {
          try {
            // Generate PDF from the rendered component with proper search timestamp
            await generatePDF(
              tempDiv.firstChild, 
              `sanctions-report-${reportData.name.replace(/\s+/g, '-')}.pdf`,
              {
                searchTimestamp: reportData.timestamp,
                searchType: 'People Search', // Mass upload is typically for people
                recordsScanned: reportData.recordsScanned || 0,
                runBy: currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
                runByEmail: currentUser?.email || '',
                organizationName: currentOrg?.name || currentOrg?.displayName || '',
                language: language
              }
            );
          } finally {
            // Clean up
            tempRoot.unmount();
            document.body.removeChild(tempDiv);
            resolve();
          }
        }, 500); // Give it more time to fully render
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Handle error - show message to user
      showError(t('errors.pdfGenerationFailed') || 'There was an error generating the PDF report. Please try again.');
    } finally {
      // Hide loading state
      const loadingIndicator = document.getElementById('pdf-loading-indicator');
      if (loadingIndicator) {
        document.body.removeChild(loadingIndicator);
      }
    }
  };

  // Function to render the detailed matches for a person
  const renderDetailedMatches = (item) => {
    // Check if we have valid detailed matches array
    if (!item.detailedMatches || !Array.isArray(item.detailedMatches) || item.detailedMatches.length === 0) {
      console.log('No match details available for:', item.name);
      return (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          <p>{t('massUpload.noDetailedMatches')}</p>
        </div>
      );
    }

    console.log('Rendering match details for:', item.name, item.detailedMatches);

    return (
      <div className="w-full">
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">{t('massUpload.matchDetails')}</h4>
        <div className="mb-3">
          {item.verificationId && (
            <Button 
              size="sm"
              onClick={() => viewFullReport(item.verificationId, item.searchId, item.name)}
            >
              <i className="fa-solid fa-file-alt mr-1.5"></i>
              {t('massUpload.viewFullReport')}
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100 dark:bg-gray-800/80">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">{t('massUpload.sanctionName')}</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">{t('massUpload.matchPercentage')}</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">{t('massUpload.confidenceLevel')}</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400">{t('massUpload.sources')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {item.detailedMatches.map((match, idx) => {
                // Normalize confidence level
                const confidenceLevel = match.confidenceLevel?.toLowerCase() || 'unknown';
                
                // Extract sources from results object
                console.log(`Match #${idx} data:`, match);
                let sources = [];
                
                // Check docData.dataSources first (match the structure from screenshots)
                if (match.docData && match.docData.dataSources) {
                  console.log(`Found dataSources in docData:`, match.docData.dataSources);
                  
                  // Handle different data structures for docData.dataSources
                  if (Array.isArray(match.docData.dataSources)) {
                    sources = [...match.docData.dataSources];
                  } 
                  else if (typeof match.docData.dataSources === 'object') {
                    // Get numeric keys from the object
                    const keys = Object.keys(match.docData.dataSources).filter(k => !isNaN(Number(k)));
                    keys.forEach(key => {
                      if (match.docData.dataSources[key]) {
                        sources.push(match.docData.dataSources[key]);
                      }
                    });
                    
                    // If that didn't work, just get all values
                    if (sources.length === 0) {
                      sources = Object.values(match.docData.dataSources).filter(Boolean);
                    }
                  }
                }
                
                // Only check top-level dataSources if we didn't find anything in docData
                if (sources.length === 0 && match.dataSources) {
                  // Handle different data structures
                  if (Array.isArray(match.dataSources)) {
                    sources = [...match.dataSources];
                  } 
                  else if (typeof match.dataSources === 'object') {
                    // Check if it has numeric keys
                    const keys = Object.keys(match.dataSources);
                    
                    // This should handle Firebase array-like objects with numeric keys
                    for (let i = 0; i < keys.length; i++) {
                      const key = keys[i];
                      if (!isNaN(Number(key))) {
                        sources.push(match.dataSources[key]);
                      }
                    }
                    
                    // If no sources found with numeric keys, try other approaches
                    if (sources.length === 0) {
                      if (match.dataSources['0'] !== undefined) {
                        // Try direct access to string keys that look like numbers
                        let i = 0;
                        while (match.dataSources[`${i}`] !== undefined) {
                          sources.push(match.dataSources[`${i}`]);
                          i++;
                        }
                      } else {
                        // Last resort: just take all values
                        sources = Object.values(match.dataSources);
                      }
                    }
                  }
                }
                
                // Fallback to results collection structure
                if (sources.length === 0) {
                  // Look in results collection formats
                  if (match.results && Array.isArray(match.results)) {
                    match.results.forEach(result => {
                      if (result.dataSources) {
                        if (Array.isArray(result.dataSources)) {
                          sources = [...result.dataSources];
                        } else if (typeof result.dataSources === 'object') {
                          sources = Object.values(result.dataSources);
                        }
                      }
                    });
                  }
                  
                  // Check other possible locations
                  if (sources.length === 0) {
                    if (match.sources) {
                      sources = Array.isArray(match.sources) ? match.sources : [match.sources];
                    } else if (match.source) {
                      sources = Array.isArray(match.source) ? match.source : [match.source];
                    } else if (match.list) {
                      sources = [match.list];
                    } else if (match.lists) {
                      sources = Array.isArray(match.lists) ? match.lists : [match.lists];
                    } else if (match.database) {
                      sources = [match.database];
                    } else if (match.program) {
                      sources = [match.program];
                    }
                  }
                }
                
                return (
                  <tr key={`${item.id}-match-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{match.fullName || match.name}</td>
                    <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">{match.unifiedScore ? Math.round(match.unifiedScore) : 0}%</td>
                    <td className="px-3 py-2 text-sm">
                      <span className={`
                        inline-block px-2 py-0.5 text-xs font-medium rounded-full
                        ${confidenceLevel.includes('very') && confidenceLevel.includes('high') ? 
                          'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : 
                          confidenceLevel.includes('high') ? 
                            'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' : 
                            confidenceLevel.includes('medium') ? 
                              'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' : 
                              'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'}
                      `}>
                        {confidenceLevel.includes('very') && confidenceLevel.includes('high') ? 
                          t('search.confidence.veryHigh') : 
                          confidenceLevel.includes('high') ? 
                            t('search.confidence.high') : 
                            confidenceLevel.includes('medium') ? 
                              t('search.confidence.medium') : 
                              t('search.confidence.low')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex flex-wrap gap-1.5">
                        {sources && sources.length > 0 ? (
                          sources.map((source, sIdx) => {
                            if (!source) return null;
                            
                            // Convert source to lowercase string for comparison
                            const sourceStr = String(source).toLowerCase();
                            // Determine source type for styling
                            let badgeColor = '';
                            
                            if (sourceStr.includes('financial') || sourceStr.includes('eu') || sourceStr.includes('ofac')) {
                              badgeColor = 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
                            } else if (sourceStr.includes('travel') || sourceStr.includes('ban')) {
                              badgeColor = 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300';
                            } else if (sourceStr.includes('un') || sourceStr.includes('united')) {
                              badgeColor = 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
                            } else {
                              badgeColor = 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
                            }
                            
                            // Format the display text to be more readable
                            const displayText = sourceStr
                              .replace(/-/g, ' ')
                              .replace(/(^|\s)\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
                              
                            return (
                              <span 
                                key={`${item.id}-match-${idx}-source-${sIdx}`} 
                                className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${badgeColor}`}
                              >
                                {displayText}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('massUpload.noSourceInfo')}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Fetch all jobs for the current user
  const fetchUserJobs = async () => {
    if (!currentUser) return;
    
    setIsLoadingJobs(true);
    clearError();
    
    try {
      // Fetch user's jobs with a limit based on jobsPerPage
              const jobs = await getUserJobs(currentOrg?.id, jobsPerPage);
      console.log("Fetched user jobs:", jobs.length);
      
      // Update the total count of fetched jobs
      setTotalJobsFetched(jobs.length);
      
      // Determine if there are potentially more jobs to load
      setHasMoreJobs(jobs.length >= jobsPerPage);
      
      // Build a map of root job IDs to their continuation chains
      const continuationChains = {};
      const processedJobIds = new Set();
      
      // First pass: Group jobs by rootJobId
      jobs.forEach(job => {
        // If this is a continuation job (has rootJobId), add it to the chain
        if (job.rootJobId && job.id !== job.rootJobId) {
          const rootJobId = job.rootJobId;
          
          // Add to the continuation chain of the root job
          if (!continuationChains[rootJobId]) {
            continuationChains[rootJobId] = [];
          }
          continuationChains[rootJobId].push(job);
          processedJobIds.add(job.id);
        }
        
        // Legacy system support - if job has originalJobId but no rootJobId
        else if (job.originalJobId && !job.rootJobId) {
          let rootJobId = job.originalJobId;
          
          // Find the very first job in the chain, not just the immediate parent
          // This handles legacy chains: A -> B -> C -> D by mapping all to A
          let parentJob = jobs.find(j => j.id === rootJobId);
          while (parentJob?.originalJobId) {
            rootJobId = parentJob.originalJobId;
            parentJob = jobs.find(j => j.id === rootJobId);
          }
          
          // Add to the continuation chain of the root job
          if (!continuationChains[rootJobId]) {
            continuationChains[rootJobId] = [];
          }
          continuationChains[rootJobId].push(job);
          processedJobIds.add(job.id);
        }
      });
      
      console.log("Continuation chains:", continuationChains);
      
      // Merge continuation job data with original jobs
      const consolidatedJobs = jobs.filter(job => !processedJobIds.has(job.id)).map(job => {
        // If this job has continuation jobs, consolidate their data
        if (continuationChains[job.id]?.length > 0) {
          const continuationJobs = continuationChains[job.id];
          
          // Sort continuation jobs by sequence number
          continuationJobs.sort((a, b) => (a.continuationSeq || 0) - (b.continuationSeq || 0));
          
          // Calculate combined statistics
          const combinedProgress = { ...job.progress };
          let combinedMatches = job.progress?.totalMatches || 0;
          let combinedHighConfidenceMatches = job.progress?.highConfidenceMatches || 0;
          let totalCompletedNames = job.progress?.completed || 0;
          let latestStatus = job.status;
          
          // Combine statistics from all continuation jobs
          continuationJobs.forEach(contJob => {
            if (contJob.progress) {
              totalCompletedNames += contJob.progress.completed || 0;
              combinedMatches += contJob.progress.totalMatches || 0;
              combinedHighConfidenceMatches += contJob.progress.highConfidenceMatches || 0;
            }
            
            // The status of the entire chain is the status of the most recent job
            // If any job is still processing, the chain is processing
            if (contJob.status === 'processing') {
              latestStatus = 'processing';
            } 
            // Otherwise take the latest non-continued status
            else if (contJob.status !== 'continued' && latestStatus === 'continued') {
              latestStatus = contJob.status;
            }
          });
          
          // Update combined progress
          combinedProgress.completed = totalCompletedNames;
          combinedProgress.totalMatches = combinedMatches;
          combinedProgress.highConfidenceMatches = combinedHighConfidenceMatches;
          
          // Create a consolidated job object
          return {
            ...job,
            status: latestStatus,
            progress: combinedProgress,
            continuationJobs: continuationJobs,
            isContinuationChain: true,
            totalJobsInChain: continuationJobs.length + 1
          };
        }
        
        return job;
      });
      
      console.log("Consolidated jobs:", consolidatedJobs.length);
      setUserJobs(consolidatedJobs);
    } catch (error) {
      console.error("Error fetching user jobs:", error);
      handleError(error, { component: 'MassUpload', action: 'fetchUserJobs' });
    } finally {
      setIsLoadingJobs(false);
    }
  };

  // Load more jobs when user clicks "Load More"
  const loadMoreJobs = async () => {
    if (!currentUser || isLoadingMoreJobs || !hasMoreJobs) return;
    
    setIsLoadingMoreJobs(true);
    clearError();
    
    try {
      // Calculate how many jobs we have already loaded
      const offset = totalJobsFetched;
      
      // Fetch the next batch of jobs
                const jobs = await getUserJobs(currentOrg?.id, jobsPerPage + offset);
      
      // Only get the new jobs beyond what we've already loaded
      const newJobs = jobs.slice(offset);
      console.log("Fetched additional jobs:", newJobs.length);
      
      // Update the total count of fetched jobs
      setTotalJobsFetched(jobs.length);
      
      // Determine if there are potentially more jobs to load
      setHasMoreJobs(newJobs.length >= jobsPerPage);
      
      // Process continuation chains logic (same as in fetchUserJobs)
      const continuationChains = {};
      const processedJobIds = new Set();
      
      // Include all jobs (existing + new) for chain processing
      const allJobs = [...userJobs, ...newJobs];
      
      // First pass: Group jobs by rootJobId
      allJobs.forEach(job => {
        if (job.rootJobId && job.id !== job.rootJobId) {
          const rootJobId = job.rootJobId;
          
          if (!continuationChains[rootJobId]) {
            continuationChains[rootJobId] = [];
          }
          continuationChains[rootJobId].push(job);
          processedJobIds.add(job.id);
        } else if (job.originalJobId && !job.rootJobId) {
          let rootJobId = job.originalJobId;
          
          let parentJob = allJobs.find(j => j.id === rootJobId);
          while (parentJob?.originalJobId) {
            rootJobId = parentJob.originalJobId;
            parentJob = allJobs.find(j => j.id === rootJobId);
          }
          
          if (!continuationChains[rootJobId]) {
            continuationChains[rootJobId] = [];
          }
          continuationChains[rootJobId].push(job);
          processedJobIds.add(job.id);
        }
      });
      
      // Merge continuation job data with original jobs
      const consolidatedJobs = allJobs.filter(job => !processedJobIds.has(job.id)).map(job => {
        if (continuationChains[job.id]?.length > 0) {
          const continuationJobs = continuationChains[job.id];
          
          continuationJobs.sort((a, b) => (a.continuationSeq || 0) - (b.continuationSeq || 0));
          
          const combinedProgress = { ...job.progress };
          let combinedMatches = job.progress?.totalMatches || 0;
          let combinedHighConfidenceMatches = job.progress?.highConfidenceMatches || 0;
          let totalCompletedNames = job.progress?.completed || 0;
          let latestStatus = job.status;
          
          continuationJobs.forEach(contJob => {
            if (contJob.progress) {
              totalCompletedNames += contJob.progress.completed || 0;
              combinedMatches += contJob.progress.totalMatches || 0;
              combinedHighConfidenceMatches += contJob.progress.highConfidenceMatches || 0;
            }
            
            if (contJob.status === 'processing') {
              latestStatus = 'processing';
            } else if (contJob.status !== 'continued' && latestStatus === 'continued') {
              latestStatus = contJob.status;
            }
          });
          
          combinedProgress.completed = totalCompletedNames;
          combinedProgress.totalMatches = combinedMatches;
          combinedProgress.highConfidenceMatches = combinedHighConfidenceMatches;
          
          return {
            ...job,
            status: latestStatus,
            progress: combinedProgress,
            continuationJobs: continuationJobs,
            isContinuationChain: true,
            totalJobsInChain: continuationJobs.length + 1
          };
        }
        
        return job;
      });
      
      console.log("Updated consolidated jobs:", consolidatedJobs.length);
      setUserJobs(consolidatedJobs);
    } catch (error) {
      console.error("Error loading more jobs:", error);
      handleError(error, { component: 'MassUpload', action: 'loadMoreJobs' });
    } finally {
      setIsLoadingMoreJobs(false);
    }
  };

  // Function to view a specific job
  const viewJob = async (jobId) => {
    console.log("Viewing job:", jobId);
    
    // Clean up any previous job data and subscription
    if (unsubscribeFunction) {
      unsubscribeFunction();
      setUnsubscribeFunction(null);
    }
    
    // Clear current job data
    setUploadedData([]);
    setJobStatus(null);
    setCompletedCount(0);
    setTotalUploadedCount(0);
    
    // Set the new job ID
    setJobId(jobId);
    setView('detail');
    
    // Update URL
    setSearchParams({ jobId });
    
    try {
      // Find the job in the user's job list to check if it's a chain
      const selectedJobFromList = userJobs.find(job => job.id === jobId);
      
      // Fetch initial job data
      console.log("Fetching initial job data for:", jobId);
      const jobData = await getJobStatus(jobId);
      console.log("Received job data, status:", jobData.status);
      
      // Check if job is continued and we should view the latest job in the chain
      if (jobData.status === 'continued' && (jobData.continuationJobId || jobData.nextJobInChain)) {
        const nextJobId = jobData.continuationJobId || jobData.nextJobInChain;
        console.log("Job is continued, redirecting to latest job in chain:", nextJobId);
        
        // Update URL with the latest job instead
        setSearchParams({ jobId: nextJobId });
        
        // Start viewing the latest job
        return viewJob(nextJobId);
      }
      
      setJobStatus(jobData);
      setIsProcessing(jobData.status === 'processing');
      
      // Function to check if job is part of a chain or has continuation
      const isPartOfChain = 
        selectedJobFromList?.isContinuationChain || 
        jobData.continuationChain || 
        jobData.rootJobId || 
        jobData.previousJobInChain || 
        jobData.continuationJobId;
      
      // Handle job chains
      if (isPartOfChain) {
        console.log("This job is part of a continuation chain. Fetching the complete chain");
        
        // Get the root job ID
        const rootJobId = jobData.rootJobId || jobId;
        
        // Fetch the complete job chain
        const jobChain = await getJobChain(rootJobId);
        
        if (!jobChain || jobChain.length === 0) {
          console.error("Unable to fetch job chain data");
          setJobStatus({
            ...jobData,
            status: jobData.status,
            error: "Unable to fetch job chain data"
          });
          
          // Still show the individual job
          await fetchResults(jobId, true);
          subscribeToJob(jobId);
          return;
        }
        
        console.log("Received job chain with", jobChain.length, "jobs:", 
          jobChain.map(j => ({ id: j.id, status: j.status })));
        
        // Determine the latest/current job in the chain to subscribe to
        const latestJobIdToSubscribe = getLatestJobInChain(jobChain);
        console.log("Latest job in chain:", latestJobIdToSubscribe);
        
        // Find the root job in the chain
        const rootJob = jobChain.find(job => !job.previousJobInChain);
        
        // Update job status with chain information
        setJobStatus({
          ...jobData,
          continuationChain: jobChain,
          isContinuationChain: true,
          rootJobId: rootJob?.id || rootJobId
        });
        
        // Fetch consolidated results from all jobs in the chain
        // Set forceRefresh to true to ensure we get all names, including pending and processed
        await fetchConsolidatedResults(rootJobId, jobChain.map(job => job.id));
        
        // Subscribe to updates for the latest job in the chain
        setTimeout(() => {
          console.log("Setting up subscription for latest job in chain:", latestJobIdToSubscribe);
          subscribeToJob(latestJobIdToSubscribe, rootJobId);
        }, 100);
      } else {
        // Standard job handling
        // First fetch results with forceRefresh=true to ensure we get all names
        await fetchResults(jobId, true);
        
        // Then subscribe to updates
      setTimeout(() => {
          console.log("Setting up subscription for standard job:", jobId);
        subscribeToJob(jobId); 
      }, 100);
      }
    } catch (error) {
      console.error("Error in viewJob:", error);
      handleError(error, { component: 'MassUpload', action: 'viewJob' });
      showError(t('errors.jobLoadFailed') || 'Failed to load job details.');
      handleClearAll(); // Go back to list view on critical error
    }
  };
  
  // Helper function to get the ID of the latest job in a chain
  const getLatestJobInChain = (jobChain) => {
    if (!jobChain || jobChain.length === 0) return null;
    
    // First check if any job is processing (highest priority)
    const processingJob = jobChain.find(job => job.status === 'processing');
    if (processingJob) return processingJob.id;
    
    // Next check for queued jobs (second priority)
    const queuedJob = jobChain.find(job => job.status === 'queued');
    if (queuedJob) return queuedJob.id;
    
    // If no processing or queued job, sort by sequence number and pick the highest
    // This should be more reliable than sorting by timestamp
    const sortedBySeq = [...jobChain].sort((a, b) => {
      // First prioritize by sequence number
      const seqA = a.continuationSeq !== undefined ? a.continuationSeq : -1;
      const seqB = b.continuationSeq !== undefined ? b.continuationSeq : -1;
      
      if (seqA !== seqB) {
        return seqB - seqA; // Descending order by sequence
      }
      
      // If sequence numbers are the same, use created timestamp as fallback
      // More recent timestamps should be considered "latest"
      const timeA = a.created ? new Date(a.created).getTime() : 0;
      const timeB = b.created ? new Date(b.created).getTime() : 0;
      return timeB - timeA; // Descending order by time
    });
    
    // Find the last non-continued job in sequence order
    // Prioritize jobs that are completed, failed, or cancelled over continued jobs
    const nonContinuedStatuses = ['completed', 'failed', 'cancelled', 'queued'];
    const latestNonContinuedJob = sortedBySeq.find(job => 
      job.status !== 'continued' && nonContinuedStatuses.includes(job.status)
    );
    if (latestNonContinuedJob) return latestNonContinuedJob.id;
    
    // Fallback: return the job with the highest sequence number
    return sortedBySeq[0]?.id || jobChain[0].id;
  };
  
  // CHANGE 3: Simplify the chain handling by integrating with fetchResults
  const fetchConsolidatedResults = async (rootJobId, jobIdsInChain) => {
    setIsLoadingConsolidatedResults(true);
    
    try {
      if (!jobIdsInChain || jobIdsInChain.length === 0) {
        console.error("No job IDs provided for fetchConsolidatedResults");
           return;
        }
      
      console.log("Fetching results for job chain:", jobIdsInChain);
      
      // First get job data for all jobs in the chain
      const jobDataPromises = jobIdsInChain.map(id => getJobStatus(id));
      const allJobData = await Promise.all(jobDataPromises);
      
      // Get results from all jobs in the chain
      const allResults = [];
      for (const jobId of jobIdsInChain) {
        try {
          const jobResults = await getJobResults(jobId);
          if (jobResults && jobResults.length > 0) {
            allResults.push(...jobResults);
          }
        } catch (error) {
          console.error(`Error fetching results for job ${jobId}:`, error);
        }
      }
      
      console.log(`Retrieved ${allResults.length} results from job chain`);
      
      // Collect various name lists from all jobs in the chain
      const allNames = new Set();
      const pendingNamesSet = new Set();
      const completedNamesSet = new Set();
      const processingNamesSet = new Set();
      
      // Track whether any job in the chain is still processing
      let isAnyJobProcessing = false;
      
      // Process all job data to collect names and statuses
      allJobData.forEach(jobData => {
        // Track overall processing status
        if (jobData.status === 'processing') {
          isAnyJobProcessing = true;
        }
        
        // Collect names from the names array if available
        if (jobData.names && Array.isArray(jobData.names)) {
          jobData.names.forEach(name => allNames.add(name));
        }
        
        // Add any pending names to the pending set
        if (jobData.pendingNames && Array.isArray(jobData.pendingNames)) {
          jobData.pendingNames.forEach(name => {
            allNames.add(name);
            pendingNamesSet.add(name);
          });
        }
        
        // Add any completed names to the completed set
        if (jobData.completedNames && Array.isArray(jobData.completedNames)) {
          jobData.completedNames.forEach(name => {
            allNames.add(name);
            completedNamesSet.add(name);
          });
        }
        
        // Add any processing names to the processing set
        if (jobData.processingNames && Array.isArray(jobData.processingNames)) {
          jobData.processingNames.forEach(name => {
            allNames.add(name);
            processingNamesSet.add(name);
          });
        }
      });
      
      console.log(`Consolidated job chain data: ${allNames.size} total names, ${completedNamesSet.size} completed, ${processingNamesSet.size} processing, ${pendingNamesSet.size} pending`);
      
      // If we still don't have names but have job sizes, create placeholders
      if (allNames.size === 0) {
        // Calculate total job size from progress
        let totalSize = 0;
        allJobData.forEach(jobData => {
          if (jobData.progress && jobData.progress.total) {
            totalSize += jobData.progress.total;
          } else if (jobData.size) {
            totalSize += jobData.size;
          }
        });
        
        // Create placeholders
        for (let i = 0; i < totalSize; i++) {
          allNames.add(`Pending Item ${i+1}`);
        }
      }
      
      // Create a map for quick result lookups, prioritizing completed results
      const resultsByName = new Map();
      
      // Sort results to prioritize completed results over pending ones
      const sortedResults = [...allResults].sort((a, b) => {
        // Prioritize completed results over pending or processing
        if (a.status === 'completed' && b.status !== 'completed') return -1;
        if (a.status !== 'completed' && b.status === 'completed') return 1;
        
        // If both have same status, prioritize ones with matches
        if ((a.matches || 0) > 0 && (b.matches || 0) === 0) return -1;
        if ((a.matches || 0) === 0 && (b.matches || 0) > 0) return 1;
        
        // If still tied, prioritize the one with more matches
        return (b.matches || 0) - (a.matches || 0);
      });
      
      // Now populate the map with prioritized results
      sortedResults.forEach(result => {
        if (!result.name) return;
        
        // Only overwrite an existing result if the new one is completed and the existing one isn't
        if (!resultsByName.has(result.name)) {
          resultsByName.set(result.name, result);
        } else if (result.status === 'completed' && resultsByName.get(result.name).status !== 'completed') {
          resultsByName.set(result.name, result);
        } else if (result.status === 'completed' && resultsByName.get(result.name).status === 'completed') {
          // If both are completed, take the one with more matches
          const existing = resultsByName.get(result.name);
              if ((result.matches || 0) > (existing.matches || 0)) {
            resultsByName.set(result.name, result);
              }
            }
          });
          
      // If we have no data yet, reconstruct the full table
      if (uploadedData.length === 0) {
        // Create a reconstructed table with all names and their current status
        const reconstructedData = Array.from(allNames).map((name, index) => {
          // Look for a result for this name
          const matchingResult = resultsByName.get(name);
          
          if (matchingResult) {
            // If we have result data, use it
            return {
            id: `name-${index}`,
              name: name,
              status: matchingResult.status || 'pending',
              matches: matchingResult.matches || 0,
              verificationId: matchingResult.verificationId,
              searchId: matchingResult.searchId,
              detailedMatches: matchingResult.details || [],
            order: index
            };
          } else {
            // No result data, determine status from job data
            let status = 'pending';
            
            if (completedNamesSet.has(name)) {
              // The name is in a completed list but we don't have results - assume completed with no matches
              status = 'completed';
            } else if (processingNamesSet.has(name)) {
              // The name is currently being processed
              status = 'processing';
            } else if (pendingNamesSet.has(name)) {
              // The name is pending processing
              status = 'pending';
            }
            
            return {
              id: `name-${index}`,
              name: name,
              status: status,
              matches: 0,
              verificationId: null,
              searchId: null,
              detailedMatches: [],
              order: index
            };
          }
        });
          
          setUploadedData(reconstructedData);
          setTotalUploadedCount(reconstructedData.length);
          setHasStartedProcessing(true);
      } 
      // If we already have data, update the existing records
      else {
          setUploadedData(prevData => {
          return prevData.map(item => {
            // Check if we have a result for this name
            const matchingResult = resultsByName.get(item.name);
            
            // If we have results, use them
            if (matchingResult) {
              return {
                ...item,
                status: matchingResult.status || item.status,
                matches: matchingResult.matches || 0,
                verificationId: matchingResult.verificationId,
                searchId: matchingResult.searchId,
                detailedMatches: matchingResult.details || []
              };
            }
            
            // If no results but we have job data about this name, use that to determine status
            if (completedNamesSet.has(item.name)) {
              // The name is marked as completed in at least one job
              return { 
                ...item, 
                status: 'completed',
                // Keep any existing match data
                matches: item.matches || 0,
                verificationId: item.verificationId,
                searchId: item.searchId
              };
            } else if (processingNamesSet.has(item.name)) {
              // The name is currently being processed in one of the jobs
              return { ...item, status: 'processing' };
            } else if (pendingNamesSet.has(item.name) && item.status !== 'completed') {
              // The name is pending in at least one job
              // Only change to pending if it's not already marked as completed
              return { ...item, status: 'pending' };
            }
            
            // If the name isn't in our job data at all, keep its current status
            return item;
          });
        });
      }
      
      // Update summary stats based on results
      updateSummaryStats(allResults);
      
      // Update progress info from combined job data
      let totalCompleted = 0;
      let totalPending = 0;
      let totalItems = 0;
      
      allJobData.forEach(jobData => {
        if (jobData.progress) {
          if (jobData.progress.completed) totalCompleted += jobData.progress.completed;
          if (jobData.progress.pending) totalPending += jobData.progress.pending;
          if (jobData.progress.total) totalItems = Math.max(totalItems, jobData.progress.total);
        }
      });
      
      setCompletedCount(totalCompleted);
      setTotalUploadedCount(totalItems || allNames.size);
      
      // Update overall processing state based on any job in the chain
      setIsProcessing(isAnyJobProcessing);
      
      // Save the consolidated data to localStorage for persistence
      if (rootJobId) {
        localStorage.setItem(`massUpload_${rootJobId}`, JSON.stringify({
          uploadedData: uploadedData,
          totalUploadedCount: totalItems || allNames.size,
          completedCount: totalCompleted
        }));
      }
      
    } catch (error) {
      console.error("Error in fetchConsolidatedResults:", error);
      showError(t('errors.resultsLoadFailed'));
    } finally {
      setIsLoadingConsolidatedResults(false);
    }
  };

  

  // Jobs List View Component
  const JobsList = () => {
    return (
      <div className="w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">{t('massUpload.jobsTitle')}</h2>
          <Button 
            onClick={createNewJob}
          >
            <i className="fa-solid fa-plus mr-2"></i>
            {t('massUpload.newJob')}
          </Button>
        </div>
        
        {isLoadingJobs ? (
          <div className="flex flex-col items-center justify-center py-12">
            <i className="fa-solid fa-spinner fa-spin text-blue-600 dark:text-blue-400 text-3xl mb-4"></i>
            <p className="text-gray-600 dark:text-gray-300">{t('massUpload.loadingJobs')}</p>
          </div>
        ) : userJobs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <i className="fa-solid fa-tasks text-gray-400 dark:text-gray-500 text-5xl mb-4"></i>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{t('massUpload.noJobs')}</p>
            <button 
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors duration-200"
              onClick={createNewJob}
            >
              {t('massUpload.startFirstJob')}
            </button>
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full table-auto">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.jobId')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.startTime')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.status')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.namesCount')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.matchesFound')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.jobType')}</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {userJobs.map(job => (
                    <tr 
                      key={job.id} 
                      className={`
                        hover:bg-gray-50 dark:hover:bg-gray-700/30
                        ${job.status === 'processing' ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                        ${job.status === 'completed-with-matches' ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}
                        ${job.status === 'error' ? 'bg-red-50 dark:bg-red-900/20' : ''}
                      `}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {job.id.substring(0, 8)}...
                        {job.isContinuationChain && (
                          <span className="ml-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-1.5 py-0.5 rounded-md">
                            Chain
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{formatDate(job.startTime)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`
                            inline-flex items-center justify-center w-5 h-5
                            ${job.status === 'processing' ? 'text-blue-600 dark:text-blue-400' : ''}
                            ${job.status === 'stopping' ? 'text-orange-600 dark:text-orange-400' : ''}
                            ${job.status === 'stopped' ? 'text-red-600 dark:text-red-400' : ''}
                            ${job.status === 'completed' ? 'text-green-600 dark:text-green-400' : ''}
                            ${job.status === 'completed-with-matches' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                            ${job.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                          `}>
                            {getStatusIcon(job.status)}
                          </span>
                          <span className={`
                            ${job.status === 'processing' ? 'text-blue-600 dark:text-blue-400' : ''}
                            ${job.status === 'stopping' ? 'text-orange-600 dark:text-orange-400' : ''}
                            ${job.status === 'stopped' ? 'text-red-600 dark:text-red-400' : ''}
                            ${job.status === 'completed' ? 'text-green-600 dark:text-green-400' : ''}
                            ${job.status === 'completed-with-matches' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                            ${job.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                            text-sm font-medium
                          `}>
                            {getStatusText(job.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {job.progress ? job.progress.total : 
                         (job.names ? job.names.length : 
                          (job.originalNames ? job.originalNames.length : 'N/A'))}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {job.progress ? job.progress.totalMatches : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {job.isContinuationChain ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">
                            <i className="fa-solid fa-link mr-1.5"></i>
                            {t('massUpload.continuationChain')} ({job.totalJobsInChain})
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                            <i className="fa-solid fa-file-lines mr-1.5"></i>
                            {t('massUpload.standardJob')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Button 
                          size="sm"
                          onClick={() => viewJob(job.id)}
                          title={t('massUpload.viewJob')}
                          className="gap-1"
                        >
                          <i className="fa-solid fa-eye mr-1"></i>
                          {t('massUpload.view')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Load More Button */}
            {hasMoreJobs && (
              <div className="flex justify-center mt-6">
                <Button
                  onClick={loadMoreJobs}
                  disabled={isLoadingMoreJobs}
                  className={`px-5 py-2 ${isLoadingMoreJobs ? 'opacity-70' : ''}`}
                >
                  {isLoadingMoreJobs ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                      {t('massUpload.loadingMore')}
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-arrow-down mr-2"></i>
                      {t('massUpload.loadMore')}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 rounded-lg shadow-sm">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <i className="fa-solid fa-circle-exclamation text-xl text-red-600 dark:text-red-400"></i>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-base font-medium text-red-800 dark:text-red-300 mb-1">
                  Error Processing File
                </h3>
                <div className="text-sm">
                  {typeof error === 'object' && error.type ? 
                    (error.type === 'BATCH_SIZE_LIMIT' 
                      ? `Batch size limit exceeded: Your file contains ${error.nameCount || 0} names, but the maximum allowed is ${MAX_BATCH_SIZE}.` 
                      : error.type === 'ACTIVE_JOB_EXISTS'
                      ? 'You already have an active batch job. Please wait for it to complete before starting a new screening.'
                      : error.rawMessage || 'An error occurred')
                    : error}
                </div>
                <div className="mt-3">
                  <button
                    className="text-sm px-3 py-1 bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 rounded hover:bg-red-300 dark:hover:bg-red-700 transition-colors"
                    onClick={clearError}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Job Detail View Component
  const JobDetailView = () => {
    return (
      <div className="w-full">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <button 
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              onClick={goBackToList}
            >
              <i className="fa-solid fa-arrow-left"></i>
              {t('massUpload.backToJobs')}
            </button>
            
            {jobId && (
              <span className="text-sm text-gray-600 dark:text-gray-400 ml-4">
                {t('massUpload.currentJob')}: <span className="font-mono text-gray-800 dark:text-gray-200">{jobId}</span>
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {jobId && jobStatus && ['processing', 'stopping'].includes(jobStatus.status) && (
              <button
                onClick={() => cancelJob(jobId)}
                className="px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-800/50 text-red-700 dark:text-red-300 rounded inline-flex items-center gap-2 transition-colors"
                disabled={isLoadingJobs || jobStatus.status === 'stopping'}
              >
                <i className="fa-solid fa-stop text-base flex-shrink-0 inline-block align-middle"></i>
                <span className="inline-block align-middle">{jobStatus.status === 'stopping' 
                  ? t('massUpload.cancelling') 
                  : t('massUpload.cancelJob')}</span>
              </button>
            )}
            
            <button
              className={`
                px-3 py-1.5 text-sm rounded-md transition-colors duration-200
                ${isProcessing ? 
                  'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' : 
                  'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'}
              `}
              onClick={handleClearAll}
              disabled={isProcessing}
            >
              {t('massUpload.clearAndNew')}
            </button>
          </div>
        </div>
        
        {/* Loading Spinner for Consolidated Results */}
        {isLoadingConsolidatedResults && (
          <div className="flex justify-center items-center p-4 mb-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 dark:border-blue-400"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-300">{t('massUpload.loadingResults')}</span>
          </div>
        )}

        {/* Job Status Panel - Only show if we have a job */}
        {jobId && jobStatus && (
          <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Status */}
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('massUpload.status')}</span>
                <div className="flex items-center">
                  <span className={`
                    mr-2 inline-flex items-center justify-center w-5 h-5
                    ${jobStatus.status === 'processing' ? 'text-blue-600 dark:text-blue-400' : ''}
                    ${jobStatus.status === 'stopping' ? 'text-orange-600 dark:text-orange-400' : ''}
                    ${jobStatus.status === 'stopped' ? 'text-red-600 dark:text-red-400' : ''}
                    ${jobStatus.status === 'completed' ? 'text-green-600 dark:text-green-400' : ''}
                    ${jobStatus.status === 'completed-with-matches' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                    ${jobStatus.status === 'completed-with-errors' ? 'text-orange-600 dark:text-orange-400' : ''}
                    ${jobStatus.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                  `}>
                    {getStatusIcon(jobStatus.status)}
                  </span>
                  <span className={`
                    text-sm font-medium
                    ${jobStatus.status === 'processing' ? 'text-blue-600 dark:text-blue-400' : ''}
                    ${jobStatus.status === 'stopping' ? 'text-orange-600 dark:text-orange-400' : ''}
                    ${jobStatus.status === 'stopped' ? 'text-red-600 dark:text-red-400' : ''}
                    ${jobStatus.status === 'completed' ? 'text-green-600 dark:text-green-400' : ''}
                    ${jobStatus.status === 'completed-with-matches' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                    ${jobStatus.status === 'completed-with-errors' ? 'text-orange-600 dark:text-orange-400' : ''}
                    ${jobStatus.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                  `}>
                    {getStatusText(jobStatus.status)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600 dark:text-gray-400">{t('massUpload.progress')}:</span>
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {jobStatus.progress?.completed || 0} / {jobStatus.progress?.total || 0}
                    {' '}
                    ({Math.round(((jobStatus.progress?.completed || 0) / (jobStatus.progress?.total || 1)) * 100)}%)
                  </span>
                </div>
              </div>
              
              {/* Matches */}
              <div className="flex flex-col space-y-1 text-sm">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('massUpload.matches')}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600 dark:text-gray-400">{t('massUpload.matchesFound')}:</span>
                  <span className="font-medium text-yellow-600 dark:text-yellow-400">
                    {jobStatus.progress?.totalMatches || 0}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600 dark:text-gray-400">{t('massUpload.highConfidenceMatches')}:</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">
                    {jobStatus.progress?.highConfidenceMatches || 0}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Child Jobs Table */}
            
          </div>
        )}

        {/* File Upload Section */}
        {!jobId && (
          <div className="mb-8">
            <div className={`
              flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg
              ${usageStats && !usageStats.allowed ? 
                'border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-50' : 
                'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'}
            `}>
              <label 
                htmlFor="csv-upload" 
                className={`
                  flex flex-col items-center justify-center p-4 mb-3 rounded-md
                  ${isUploading || isProcessing || (usageStats && !usageStats.allowed) ? 
                    'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' : 
                    'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer'}
                `}
              >
                <i className="fa-solid fa-upload text-2xl mb-2"></i>
                <span>
                  {usageStats && !usageStats.allowed ? 
                    t('massUpload.limitReached') || 'Monthly search limit reached' : 
                    t('massUpload.selectFile')}
                </span>
              </label>
              <input
                type="file"
                id="csv-upload"
                accept=".csv,.txt"
                ref={fileInputRef}
                onChange={handleFileUpload}
                disabled={isUploading || isProcessing || (usageStats && !usageStats.allowed)}
                className="hidden"
              />
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 text-center">
                <p className="mb-1">{t('massUpload.fileInfo') || 'Upload a CSV or TXT file with one name per line'}</p>
                <p className="text-amber-600 dark:text-amber-400 font-medium">{t('massUpload.batchSizeLimit') || 'Maximum 250 names per batch'}</p>
                <p className="text-amber-600 dark:text-amber-400 font-medium">{t('massUpload.oneJobLimit') || 'Only one active batch job allowed at a time'}</p>
                
                {/* Download Sample button */}
                <button
                  onClick={downloadSampleCSV}
                  className="mt-3 px-3 py-1.5 rounded-md text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <i className="fa-solid fa-download mr-1.5"></i>
                  {t('massUpload.downloadSample') || 'Download Sample CSV'}
                </button>
              </div>
              {uploadedData.length > 0 && (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                  <span>{t('massUpload.namesLoaded', { count: totalUploadedCount })}</span>
                </div>
              )}
            </div>

            {/* Warning for insufficient capacity */}
            {uploadedData.length > 0 && usageStats && usageStats.allowed && uploadedData.length > (usageStats.remaining || 0) && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
                <div className="flex items-start">
                  <i className="fa-solid fa-exclamation-triangle text-yellow-500 mt-0.5 mr-3"></i>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Batch Size Exceeds Available Searches
                    </h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      This batch contains {uploadedData.length} names, but you only have {usageStats.remaining || 0} searches remaining this month. 
                      Please reduce your batch size or upgrade your plan to continue.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Screening Controls */}
            {uploadedData.length > 0 && (
              <div className="flex justify-center mt-6">
                <button
                  className={`
                    px-5 py-2.5 rounded-md flex items-center gap-2 font-medium text-white transition-colors
                    ${isProcessing || uploadedData.length === 0 || (usageStats && !usageStats.allowed) || (usageStats && uploadedData.length > (usageStats.remaining || 0)) ? 
                      'bg-blue-400 dark:bg-blue-600 cursor-not-allowed' : 
                      'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'}
                  `}
                  onClick={handleStartScreening}
                  disabled={isProcessing || uploadedData.length === 0 || (usageStats && !usageStats.allowed) || (usageStats && uploadedData.length > (usageStats.remaining || 0))}
                >
                  {isProcessing ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      {t('massUpload.processing')}
                    </>
                  ) : (usageStats && usageStats.allowed && uploadedData.length > (usageStats.remaining || 0)) ? (
                    <>
                      <i className="fa-solid fa-exclamation-triangle"></i>
                      {`Insufficient Capacity (${uploadedData.length}/${usageStats.remaining || 0})`}
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-play"></i>
                      {hasStartedProcessing ? t('massUpload.resume') : t('massUpload.startScreening')}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Job Status Information */}
        {jobId && jobStatus &&  (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('massUpload.status')}:</span>
                <span className={`
                  text-sm font-medium 
                  ${jobStatus.status === 'processing' ? 'text-blue-600 dark:text-blue-400' : ''}
                  ${jobStatus.status === 'completed' ? 'text-green-600 dark:text-green-400' : ''}
                  ${jobStatus.status === 'completed-with-matches' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                  ${jobStatus.status === 'failed' ? 'text-red-600 dark:text-red-400' : ''}
                  ${jobStatus.status === 'cancelled' ? 'text-gray-600 dark:text-gray-400' : ''}
                `}>
                  {jobStatus.status}
                </span>
              </div>
              {jobStatus.message && (
                <span className="text-sm text-gray-600 dark:text-gray-400">{jobStatus.message}</span>
              )}
            </div>
            {/* Add download button when job is complete */}
            {(jobStatus.status === 'completed' || jobStatus.status === 'failed') && (
              <button 
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-full transition-colors"
                onClick={downloadResultsCSV}
                title={t('massUpload.downloadResults') || 'Download Results as CSV'}
              >
                <i className="fa-solid fa-download"></i>
              </button>
            )}
          </div>
        )}

        {/* Continuation Chain Information - Show for jobs that are part of a chain */}
        {jobId && jobStatus && jobStatus.continuationChain && jobStatus.continuationChain.length > 1 && (
          <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                <i className="fa-solid fa-link mr-2"></i>
                {t('massUpload.continuationChain')}
              </h3>
              <span className="text-sm bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 px-2 py-0.5 rounded-md">
                {jobStatus.continuationChain.length} {t('massUpload.jobsInChain')}
              </span>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {t('massUpload.continuationExplanation')}
            </p>
            
            <ContinuationChainTable 
              chainData={jobStatus.continuationChain}
              formatDate={formatDate}
              getStatusIcon={getStatusIcon}
              getStatusText={getStatusText}
              t={t}
            />
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 rounded-md flex items-start gap-2">
            <i className="fa-solid fa-exclamation-circle mt-0.5"></i>
            <span>
              {typeof error === 'object' && error.type === 'BATCH_SIZE_LIMIT' 
                ? t('massUpload.batchSizeLimitError', { count: error.nameCount }) 
                : typeof error === 'object' && error.type === 'ACTIVE_JOB_EXISTS'
                ? t('massUpload.activeJobError') 
                : error}
            </span>
          </div>
        )}

        {/* Progress Bar - Show when processing or at least one item is completed */}
        {(isProcessing || completedCount > 0) && uploadedData.length > 0 && (!jobStatus || !jobStatus.isParentJob) && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('massUpload.progress')}: {completedCount} / {totalUploadedCount}</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{calculateProgress()}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 dark:bg-blue-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${calculateProgress()}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Results Summary - Show after processing is complete */}
        {showSummary && !isProcessing && summaryStats.totalProcessed > 0 && (
          <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">{t('massUpload.summaryTitle')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md">
                <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">{t('massUpload.totalProcessed')}:</span>
                <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">{summaryStats.totalProcessed}</span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md">
                <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">{t('massUpload.namesWithMatches')}:</span>
                <span className="text-xl font-semibold text-yellow-600 dark:text-yellow-400">{summaryStats.totalWithMatches}</span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md">
                <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">{t('massUpload.namesWithoutMatches')}:</span>
                <span className="text-xl font-semibold text-green-600 dark:text-green-400">{summaryStats.totalNoMatches}</span>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md">
                <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">{t('massUpload.totalMatchesFound')}:</span>
                <span className="text-xl font-semibold text-orange-600 dark:text-orange-400">{summaryStats.totalMatches}</span>
              </div>
            </div>
          </div>
        )}

        {/* Names Table */}
        {uploadedData.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full table-auto">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.name')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.status')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.matches')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.verificationId')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 dark:text-gray-300">{t('massUpload.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {uploadedData
                  .slice() // Create a copy to avoid modifying the original array
                  .sort((a, b) => a.order - b.order) // Sort by original order
                  .map((item, index) => (
                  <React.Fragment key={item.id}>
                    <tr 
                      className={`
                        ${item.status === 'processing' ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                        ${expandedRowId === item.id ? 'bg-gray-100 dark:bg-gray-800/70' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}
                        transition-colors duration-150
                      `}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{item.name}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`
                            inline-flex items-center justify-center w-5 h-5
                            ${item.status === 'pending' ? 'text-gray-400 dark:text-gray-500' : ''}
                            ${item.status === 'processing' ? 'text-blue-600 dark:text-blue-400' : ''}
                            ${item.status === 'completed' ? 'text-green-600 dark:text-green-400' : ''}
                            ${item.status === 'completed-with-matches' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                            ${item.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                          `}>
                            {getStatusIcon(item.status)}
                          </span>
                          <span className={`
                            ${item.status === 'pending' ? 'text-gray-500 dark:text-gray-400' : ''}
                            ${item.status === 'processing' ? 'text-blue-600 dark:text-blue-400' : ''}
                            ${item.status === 'completed' ? 'text-green-600 dark:text-green-400' : ''}
                            ${item.status === 'completed-with-matches' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                            ${item.status === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                            text-sm font-medium
                          `}>
                            {getStatusText(item.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {item.status === 'completed' || item.status === 'completed-with-matches' ? 
                          item.matches : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {item.verificationId ? (
                          <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{item.verificationId}</span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          {handleActions(item)}
                        </div>
                      </td>
                    </tr>
                    {expandedRowId === item.id && (
                      <tr className="bg-gray-50 dark:bg-gray-800/30">
                        <td colSpan="5" className="px-4 py-4">
                          {renderDetailedMatches(item)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State - Only show when we don't have a job ID */}
        {uploadedData.length === 0 && !isUploading && !jobId && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <i className="fa-solid fa-file-csv text-gray-300 dark:text-gray-600 text-5xl mb-4"></i>
            <p className="text-gray-600 dark:text-gray-400 mb-2">{t('massUpload.emptyStateMessage')}</p>
          </div>
        )}

        {/* Loading State for File Upload */}
        {isUploading && (
          <div className="flex flex-col items-center justify-center py-12">
            <i className="fa-solid fa-spinner fa-spin text-blue-600 dark:text-blue-400 text-3xl mb-4"></i>
            <p className="text-gray-600 dark:text-gray-400">{t('massUpload.uploading')}</p>
          </div>
        )}
      </div>
    );
  };

  // New function to fetch monitoring status of items
  useEffect(() => {
    const fetchMonitoringStatus = async () => {
      if (!currentUser || !currentOrg || uploadedData.length === 0) return;
      
      try {
        const verificationIds = uploadedData
          .filter(item => item.verificationId)
          .map(item => item.verificationId);
        
        if (verificationIds.length === 0) return;
        
        // Firebase "in" queries are limited to 30 items
        // Split verification IDs into chunks of 30 or fewer
        const chunks = [];
        for (let i = 0; i < verificationIds.length; i += 30) {
          chunks.push(verificationIds.slice(i, i + 30));
        }
        
        console.log(`Splitting ${verificationIds.length} verification IDs into ${chunks.length} chunks for monitoring status check`);
        
        // Create a combined result object
        const monitored = {};
        
        // Process each chunk with a separate query
        for (const chunk of chunks) {
          const q = query(
            collection(db, 'monitoring'),
            where('orgId', '==', currentOrg.id),
            where('verificationId', 'in', chunk)
          );
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach((doc) => {
            monitored[doc.data().verificationId] = doc.id;
          });
        }
        
        setMonitoredItems(monitored);
      } catch (error) {
        console.error('Error fetching monitoring status:', error);
      }
    };
    
    fetchMonitoringStatus();
  }, [currentUser, uploadedData]);

  // Function to add a search to monitoring
  const addToMonitoring = async (verificationId, name) => {
    if (!currentUser || !currentOrg || isAddingToMonitoring) return;
    
    setIsAddingToMonitoring(true);
    
    try {
      // If already monitored, remove it
      if (monitoredItems[verificationId]) {
        await deleteDoc(doc(db, 'monitoring', monitoredItems[verificationId]));
        
        // Update local state
        setMonitoredItems(prev => {
          const updated = { ...prev };
          delete updated[verificationId];
          return updated;
        });
        
        showSuccess(t('monitoring.removedSuccess') || 'Successfully removed from monitoring');
      } else {
        // Add to monitoring
        const monitoringRef = await addDoc(collection(db, 'monitoring'), {
          userId: currentUser.uid,
          orgId: currentOrg.id,
          searchTerm: name,
          verificationId: verificationId,
          createdAt: serverTimestamp(),
          lastSearchDate: serverTimestamp()
        });
        
        // Update local state
        setMonitoredItems(prev => ({
          ...prev,
          [verificationId]: monitoringRef.id
        }));
        
        showSuccess(t('monitoring.addedSuccess') || 'Successfully added to monitoring');
      }
    } catch (error) {
      console.error('Error updating monitoring:', error);
      showError(error.message || t('monitoring.actionError') || 'Error updating monitoring status');
    } finally {
      setIsAddingToMonitoring(false);
    }
  };

  // Add this new function to handle cancelling jobs
  const cancelJob = async (jobId) => {
    try {
      setIsLoadingJobs(true);
      
      // Call the Firebase function to cancel the job
      const cancelJobFunction = httpsCallable(functions, 'cancelBatchJob');
      const result = await cancelJobFunction({ jobId });
      
      if (result.data.success) {
        showSuccess(t('massUpload.jobCancellationRequested'));
        
        // Refresh job list after a short delay to see the status change
        setTimeout(() => {
          fetchUserJobs();
        }, 2000);
      } else {
        showError(result.data.message || t('massUpload.couldNotCancelJob'));
      }
    } catch (error) {
      console.error("Error cancelling job:", error);
      showError(t('massUpload.failedToCancelJob') + ": " + error.message);
    } finally {
      setIsLoadingJobs(false);
    }
  };
  
  // Function to handle actions for a specific item
  const handleActions = (item) => {
    if (item.matches > 0 && (item.status === 'completed' || item.status === 'completed-with-matches')) {
      return (
        <>
          <button 
            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded inline-flex items-center gap-1 transition-colors"
            onClick={() => toggleRowExpansion(item.id)}
          >
            <i className={`fa-solid ${expandedRowId === item.id ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
            {expandedRowId === item.id 
              ? t('massUpload.hideMatches') 
              : t('massUpload.viewMatches')}
          </button>
        </>
      );
    } else if (item.matches === 0 && item.status === 'completed' && item.verificationId) {
      return (
        <>
          <button 
            className="px-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 rounded-full transition-colors mr-2"
            onClick={() => downloadPdfReport(item.verificationId, item.name)}
            title={t('massUpload.downloadPdf') || 'Download PDF'}
          >
            <i className="fa-solid fa-file-pdf text-lg"></i>
          </button>
          <button 
            className={`
              px-2 rounded-full transition-colors
              ${isAddingToMonitoring ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : ''}
              ${monitoredItems[item.verificationId] ? 
                'text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300' : 
                'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'}
            `}
            onClick={() => addToMonitoring(item.verificationId, item.name)}
            title={monitoredItems[item.verificationId] ? 
              (t('monitoring.removeFromMonitoring') || 'Remove from monitoring') : 
              (t('monitoring.addToMonitoring') || 'Add to monitoring')}
            disabled={isAddingToMonitoring}
          >
            <i className={`fa-solid ${monitoredItems[item.verificationId] ? 'fa-eye-slash' : 'fa-shield'} text-lg`}></i>
          </button>
        </>
      );
    }
    return null;
  };

  // Function to download results as CSV
  const downloadResultsCSV = () => {
    if (uploadedData.length === 0) return;
    
    try {
      // Create CSV content
      let csvContent = "Name,Status,Matches,VerificationID\n";
      
      uploadedData.forEach(item => {
        // Format status text for CSV
        const statusText = getStatusText(item.status);
        
        // Create a CSV row with quotes around each field to handle commas in names
        const row = [
          `"${item.name}"`,
          `"${statusText}"`,
          item.matches,
          item.verificationId || ''
        ].join(',');
        
        csvContent += row + "\n";
      });
      
      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Set download attributes
      const filename = `sanctions-screening-results-${new Date().toISOString().slice(0, 10)}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      
      // Append to document, trigger download, and clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess(t('massUpload.downloadSuccess') || 'Results downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      showError(t('errors.downloadFailed') || 'Failed to download results');
    }
  };

  // Function to create a new job
  const createNewJob = () => {
    // Clear all data
    handleClearAll();
    
    // Switch to detail view with no job ID
    setView('detail');
    setSearchParams({});
  };

  // Function to format date from timestamp
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Render function that ensures we always display the complete results for parent jobs
  const getDataToDisplay = () => { return uploadedData; };

  const downloadSampleCSV = () => {
    // Create a list of 10 European names
    const sampleNames = [
      "Sophie Muller",
      "Jean-Pierre Dupont",
      "Elisa Rossi",
      "Anders Johansson",
      "Maria Garcia",
      "Anna Kowalski",
      "Luca Bianchi",
      "Nikolai Petrov",
      "Emma Nielsen",
      "Pierre Dubois"
    ];
    
    // Create CSV content with header
    const csvContent = "name\n" + sampleNames.join("\n");
    
    // Create a blob with the CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    
    // Create a temporary URL for the blob
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link element to trigger the download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "mass_upload_template.csv");
    document.body.appendChild(link);
    
    // Trigger the download
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Function to check if the user has any active jobs
  const checkActiveJobs = async () => {
    if (!currentUser || !currentOrg) {
      console.log('Cannot check active jobs: missing user or organization');
      return { hasActiveJobs: false, activeJobId: null };
    }
    
    try {
      // Query by organization first (as required by Firebase rules), then filter by user
      const orgJobsQuery = query(
        collection(db, 'batch-jobs'),
        where('orgId', '==', currentOrg?.id),
        where('status', 'in', ['pending', 'processing'])
      );
      
      const activeJobsSnapshot = await getDocs(orgJobsQuery);
      
      // Filter to only jobs for this specific user
      const userActiveJobs = activeJobsSnapshot.docs.filter(doc => 
        doc.data().userId === currentUser?.uid
      );
      
      // Log details about the query and results for debugging
      console.log(`Active Jobs Query - Org ID: ${currentOrg?.id}, User ID: ${currentUser?.uid}, Status: ['pending', 'processing']`);
      console.log(`Found ${activeJobsSnapshot.size} org jobs, ${userActiveJobs.length} user jobs`);
      
      if (userActiveJobs.length > 0) {
        // Get the first active job document
        const activeJobDoc = userActiveJobs[0];
        const activeJobData = activeJobDoc.data();
        
        // Log the active job details
        console.log('Active job found:', {
          id: activeJobDoc.id,
          status: activeJobData.status,
          startTime: activeJobData.startTime,
          progress: activeJobData.progress
        });
        
        // User has active jobs
        return {
          hasActiveJobs: true,
          activeJobId: activeJobDoc.id,
          activeJobDetails: activeJobData
        };
      }
      
      // No active jobs
      return {
        hasActiveJobs: false,
        activeJobId: null
      };
    } catch (error) {
      console.error("Error checking active jobs:", error);
      return {
        hasActiveJobs: false,
        activeJobId: null,
        error
      };
    }
  };

  // Add the useEffect hook after defining the state variables
  
  // Check for active jobs when component loads in detail view or when a new file is uploaded
  useEffect(() => {
    if (view === 'detail' && uploadedData.length > 0 && !isProcessing && !jobId) {
      // Check for active jobs
      const checkForActiveJobs = async () => {
        try {
          const activeJobCheck = await checkActiveJobs();
          
          if (activeJobCheck.hasActiveJobs) {
            // Show a warning to the user without the job ID
            const activeJobError = new Error('You already have an active batch job. Please wait for it to complete before starting a new screening.');
            activeJobError.code = 'active-job-exists';
            activeJobError.activeJobId = activeJobCheck.activeJobId; // Include job ID for console logs only
            
            // Explicit console log for debugging
            console.log(`Proactive check found active job - Active job ID: ${activeJobCheck.activeJobId}`, activeJobCheck);
            
            handleError(activeJobError, { component: 'MassUpload', action: 'checkActiveJobs' });
          }
        } catch (error) {
          console.error("Error checking active jobs:", error);
        }
      };
      
      checkForActiveJobs();
    }
  }, [view, uploadedData.length, isProcessing, jobId, currentUser]);

  // Listen for search param changes
  useEffect(() => {
    const handleSearchParamsChange = () => {
      setView(searchParams.get('jobId') ? 'detail' : 'list');
    };
    handleSearchParamsChange();
  }, [searchParams]);

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-6">
      {/* Search Limit Warning - Show for authenticated users only */}
      {currentUser && currentOrg && (
        <SearchLimitWarning 
          usageStats={usageStats} 
          loading={usageLoading}
        />
      )}
      
      <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">{t('massUpload.title')}</h2>
      <p className="text-gray-600 dark:text-gray-300 mb-6">{t('massUpload.description')}</p>

      {view === 'list' ? <JobsList /> : <JobDetailView />}
    </div>
  );
};

export default MassUpload;